import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { motion, useReducedMotion } from "framer-motion";
import { ChevronDown, ChevronUp, Eye, FileText, Pause, Pencil, Play, Plus, RotateCcw, UserPlus2 } from "lucide-react";
import { API, api, formatApiError } from "../../api";
import { DESK } from "../../constants/testIds";
import { formatCurrency } from "../../lib/format";
import { fadeUp } from "../../lib/motion";
import { Button } from "../../components/ui/button";
import { StatusPill } from "../../components/common/StatusPill";
import { ErrorState } from "../../components/common/ErrorState";
import { GymLoader } from "../../components/common/GymLoader";
import { EditMemberModal } from "../../components/desk/EditMemberModal";
import { ReactivateModal } from "../../components/desk/ReactivateModal";
import { RenewModal } from "../../components/desk/RenewModal";
import { AddFamilyModal } from "../../components/desk/AddFamilyModal";
import { AddPlanModal } from "../../components/desk/AddPlanModal";
import { MemberProfileSection } from "../../components/common/MemberProfileSection";
import { SuspendDialog } from "../../components/common/SuspendDialog";
import { BookMemberIntoClassDialog } from "../../components/scheduling/BookMemberIntoClassDialog";
import { toast } from "../../components/ui/sonner";

// UX-POLISH-01 #10: a member can hold multiple concurrent plans (e.g. gym + PT) — this page now
// lists ALL of them via `GET /members/{id}/memberships` (new endpoint), grouped to one row per
// DISTINCT plan (the most recent enrollment for that plan), with Freeze/Renew/Resume scoped to
// that specific membership. Older/expired enrollments collapse into a "History" toggle instead
// of cluttering the primary view.
export default function MemberDetail() {
  const { memberId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const reducedMotion = useReducedMotion();
  const queryClient = useQueryClient();
  const [modal, setModal] = useState(null); // "edit" | "reactivate" | "renew" | "family" | "addPlan"
  const [activeMembership, setActiveMembership] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);

  const memberQ = useQuery({
    queryKey: ["desk-member", memberId],
    queryFn: async () => (await api.get(`/members/${memberId}`)).data,
  });

  const balanceQ = useQuery({
    queryKey: ["desk-member-balance", memberId],
    queryFn: async () => (await api.get(`/members/${memberId}/balance`)).data,
  });

  const ledgerQ = useQuery({
    queryKey: ["desk-member-ledger", memberId],
    queryFn: async () => (await api.get("/ledger", { params: { member_ref: memberId } })).data.entries,
  });

  const membershipsQ = useQuery({
    queryKey: ["desk-member-memberships", memberId],
    queryFn: async () => (await api.get(`/members/${memberId}/memberships`)).data.memberships,
  });

  const invoicesQ = useQuery({
    queryKey: ["desk-member-invoices", memberId],
    queryFn: async () => (await api.get("/invoices", { params: { member_ref: memberId } })).data.invoices,
  });
  const invoiceByPayment = useMemo(
    () => Object.fromEntries((invoicesQ.data || []).filter((i) => i.payment_ref).map((i) => [i.payment_ref, i])),
    [invoicesQ.data],
  );

  const generateInvoiceMutation = useMutation({
    mutationFn: (paymentRef) => api.post("/invoices/for-payment", { payment_ref: paymentRef }),
    onSuccess: (res) => {
      window.open(`${API}/invoices/${res.data.id}/pdf`, "_blank");
      queryClient.invalidateQueries({ queryKey: ["desk-member-invoices", memberId] });
    },
    onError: (e) => toast.error(formatApiError(e).message),
  });

  const plansQ = useQuery({ queryKey: ["desk-plans"], queryFn: async () => (await api.get("/plans")).data.plans });
  const planById = useMemo(() => Object.fromEntries((plansQ.data || []).map((p) => [p.id, p])), [plansQ.data]);

  const { current, history } = useMemo(() => {
    const all = membershipsQ.data || [];
    const latestByPlan = new Map();
    for (const m of all) {
      if (m.status !== "active" && m.status !== "frozen") continue;
      const existing = latestByPlan.get(m.plan_ref);
      if (!existing || m.state.start > existing.state.start) latestByPlan.set(m.plan_ref, m);
    }
    const currentList = Array.from(latestByPlan.values()).sort((a, b) => (a.state.start < b.state.start ? 1 : -1));
    const currentIds = new Set(currentList.map((m) => m.id));
    const historyList = all.filter((m) => !currentIds.has(m.id));
    return { current: currentList, history: historyList };
  }, [membershipsQ.data]);

  useEffect(() => {
    if (searchParams.get("reactivate") === "1" && memberQ.data) {
      setModal("reactivate");
      searchParams.delete("reactivate");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, memberQ.data, setSearchParams]);

  // DEFECT-D-18: `?renew=1` (from a search-result quick action) auto-opens Renew on this
  // member's most current membership.
  useEffect(() => {
    if (searchParams.get("renew") === "1" && current.length > 0) {
      setActiveMembership(current[0]);
      setModal("renew");
      searchParams.delete("renew");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, current, setSearchParams]);

  const freezeMutation = useMutation({
    mutationFn: (m) => api.post(`/memberships/${m.id}/freeze`, { expected_version: m._sync.version }, { headers: { "Idempotency-Key": crypto.randomUUID() } }),
    onSuccess: () => {
      toast.success("Membership frozen.");
      queryClient.invalidateQueries({ queryKey: ["desk-member-memberships", memberId] });
    },
    onError: (e) => toast.error(formatApiError(e).message),
  });

  const resumeMutation = useMutation({
    mutationFn: (m) => api.post(`/memberships/${m.id}/resume`, { expected_version: m._sync.version }, { headers: { "Idempotency-Key": crypto.randomUUID() } }),
    onSuccess: () => {
      toast.success("Membership resumed.");
      queryClient.invalidateQueries({ queryKey: ["desk-member-memberships", memberId] });
    },
    onError: (e) => toast.error(formatApiError(e).message),
  });

  // DEFECT-D-05: reason-required, immediate-effect suspend for a member (distinct from a lapsed
  // membership — this blocks the member's own app login entirely, not just their gym access).
  const [suspendOpen, setSuspendOpen] = useState(false);
  const suspendMutation = useMutation({
    mutationFn: (reason) => api.post(`/members/${memberId}/suspend`, { reason }),
    onSuccess: () => { toast.success("Member suspended."); setSuspendOpen(false); queryClient.invalidateQueries({ queryKey: ["desk-member", memberId] }); },
    onError: (e) => toast.error(formatApiError(e).message),
  });
  const unsuspendMutation = useMutation({
    mutationFn: () => api.post(`/members/${memberId}/unsuspend`),
    onSuccess: () => { toast.success("Member reinstated."); queryClient.invalidateQueries({ queryKey: ["desk-member", memberId] }); },
    onError: (e) => toast.error(formatApiError(e).message),
  });

  if (memberQ.isError) return <ErrorState error={memberQ.error} onRetry={() => memberQ.refetch()} title="Couldn't load this member" />;
  if (memberQ.isLoading) return <GymLoader fullPage label="Loading member…" />;

  const member = memberQ.data;
  const isLapsed = member.status !== "active" || current.length === 0;

  return (
    <div data-testid={DESK.memberDetailPage} className="space-y-5 max-w-3xl">
      <motion.div {...fadeUp(reducedMotion)} className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2" style={{ color: "#141416" }}>
            {member.profile.name}
            <StatusPill status={member.status === "active" ? "active" : "overdue"} label={member.status === "suspended" ? "Suspended" : member.status === "active" ? "Active" : "Lapsed"} />
          </h1>
          <p className="text-sm mt-0.5" style={{ color: "#68686E" }}>{member.profile.phone} {member.profile.email ? `· ${member.profile.email}` : ""}</p>
          {member.status === "suspended" && member.suspension?.reason && (
            <p className="text-xs mt-1" style={{ color: "#68686E" }}>Suspended: "{member.suspension.reason}"</p>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" data-testid={DESK.memberEditButton} onClick={() => setModal("edit")} className="gap-1.5"><Pencil size={13} /> Edit</Button>
          <Button variant="outline" size="sm" data-testid={DESK.memberBookClassButton} onClick={() => setModal("bookClass")} className="gap-1.5"><UserPlus2 size={13} /> Book into class</Button>
          {member.status === "suspended" ? (
            <Button variant="outline" size="sm" data-testid={DESK.memberUnsuspendButton} onClick={() => unsuspendMutation.mutate()} disabled={unsuspendMutation.isPending}>Unsuspend</Button>
          ) : (
            <Button variant="outline" size="sm" data-testid={DESK.memberSuspendButton} onClick={() => setSuspendOpen(true)} style={{ color: "#D4183D" }}>Suspend</Button>
          )}
        </div>
      </motion.div>

      <motion.div {...fadeUp(reducedMotion, 0.03)}>
        <MemberProfileSection member={member} idPrefix="desk-member" />
      </motion.div>

      <motion.div {...fadeUp(reducedMotion, 0.04)} className="grid grid-cols-2 gap-3">
        <div className="rounded-[14px] p-4" style={{ background: "#fff", border: "1px solid #EBEAE5" }}>
          <p className="eyebrow mb-1">Dues balance</p>
          <p className="text-xl font-bold" style={{ color: balanceQ.data?.dues_balance > 0 ? "var(--scarlet-neg)" : "#141416" }}>
            {formatCurrency(balanceQ.data?.dues_balance ?? 0)}
          </p>
        </div>
        <div className="rounded-[14px] p-4" style={{ background: "#fff", border: "1px solid #EBEAE5" }}>
          <p className="eyebrow mb-1">Active plans</p>
          <p className="text-xl font-bold" style={{ color: "#141416" }}>{current.length}</p>
        </div>
      </motion.div>

      <motion.div {...fadeUp(reducedMotion, 0.06)} className="space-y-2">
        <div className="flex items-center justify-between">
          <p className="eyebrow">Plans</p>
          <Button data-testid={DESK.memberAddPlanButton} size="sm" variant="outline" onClick={() => setModal("addPlan")} className="gap-1.5"><Plus size={13} /> Add another plan</Button>
        </div>
        {isLapsed && (
          <div className="rounded-[14px] p-4" style={{ background: "#fff", border: "1px solid #EBEAE5" }}>
            <p className="text-sm mb-2" style={{ color: "#68686E" }}>No active plan on file.</p>
            <Button data-testid={DESK.memberReactivateOpenButton} onClick={() => setModal("reactivate")} className="text-white gap-1.5" style={{ background: "#D6202B" }}>
              <RotateCcw size={14} /> Reactivate
            </Button>
          </div>
        )}
        {current.map((m) => {
          const plan = planById[m.plan_ref];
          return (
            <div key={m.id} data-testid={DESK.membershipRow(m.id)} className="rounded-[14px] p-4 flex items-center justify-between flex-wrap gap-2" style={{ background: "#fff", border: "1px solid #EBEAE5" }}>
              <div>
                <p className="text-sm font-semibold flex items-center gap-2" style={{ color: "#141416" }}>
                  {plan?.name || m.plan_ref}
                  <StatusPill status={m.status === "active" ? "active" : "overdue"} label={m.status} />
                  {m.discount_percent > 0 && <span className="status-pill">{m.discount_percent}% off</span>}
                </p>
                <p className="text-xs mt-0.5" style={{ color: "#68686E" }}>
                  {m.state.end ? `ends ${m.state.end}` : m.state.remaining_sessions != null ? `${m.state.remaining_sessions} sessions left` : "no expiry"}
                </p>
              </div>
              <div className="flex gap-2">
                {m.status === "active" && (
                  <Button size="sm" variant="outline" onClick={() => { setActiveMembership(m); setModal("renew"); }} data-testid={DESK.memberRenewButton}>Renew / change plan</Button>
                )}
                {m.status === "active" && (
                  <Button size="sm" variant="outline" onClick={() => freezeMutation.mutate(m)} disabled={freezeMutation.isPending} data-testid={DESK.memberFreezeButton} className="gap-1.5">
                    <Pause size={13} /> Freeze
                  </Button>
                )}
                {m.status === "frozen" && (
                  <Button size="sm" onClick={() => resumeMutation.mutate(m)} disabled={resumeMutation.isPending} data-testid={DESK.memberResumeButton} className="text-white gap-1.5" style={{ background: "#D6202B" }}>
                    <Play size={13} /> Resume
                  </Button>
                )}
              </div>
            </div>
          );
        })}
        {history.length > 0 && (
          <div>
            <button onClick={() => setHistoryOpen((v) => !v)} className="flex items-center gap-1.5 text-xs font-medium mt-1" style={{ color: "#68686E" }}>
              {historyOpen ? <ChevronUp size={13} /> : <ChevronDown size={13} />} {history.length} older enrollment(s)
            </button>
            {historyOpen && (
              <div className="rounded-[14px] overflow-hidden mt-2" style={{ background: "#fff", border: "1px solid #EBEAE5" }}>
                {history.slice(0, 30).map((m) => (
                  <div key={m.id} className="flex items-center justify-between px-4 py-2 text-xs" style={{ borderBottom: "1px solid #F4F3EF", color: "#68686E" }}>
                    <span>{planById[m.plan_ref]?.name || m.plan_ref} · {m.state.start}</span>
                    <span className="status-pill">{m.status}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </motion.div>

      <motion.div {...fadeUp(reducedMotion, 0.1)} className="flex flex-wrap gap-2">
        <Button data-testid={DESK.memberAddFamilyButton} onClick={() => setModal("family")} variant="outline" className="gap-1.5">
          <UserPlus2 size={13} /> Add family member
        </Button>
      </motion.div>

      <motion.div {...fadeUp(reducedMotion, 0.12)}>
        <p className="eyebrow mb-2">Ledger history</p>
        <div data-testid={DESK.memberLedgerTable} className="rounded-[14px] overflow-hidden" style={{ background: "#fff", border: "1px solid #EBEAE5" }}>
          {(!ledgerQ.data || ledgerQ.data.length === 0) && <p className="p-4 text-sm" style={{ color: "#68686E" }}>No ledger entries yet.</p>}
          {ledgerQ.data?.slice().reverse().slice(0, 25).map((e) => {
            const isPayment = e.related_type === "payment" && e.direction === "credit" && !!e.related_ref;
            const invoice = isPayment ? invoiceByPayment[e.related_ref] : null;
            return (
              <div key={e._id} className="flex items-center justify-between px-4 py-2.5 text-sm" style={{ borderBottom: "1px solid #EBEAE5" }}>
                <div>
                  <span className="font-medium" style={{ color: "#141416" }}>{e.type.replace(/_/g, " ")}</span>
                  <span className="ml-2 text-xs" style={{ color: "#68686E" }}>{new Date(e.at).toLocaleDateString()}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-semibold" style={{ color: e.direction === "credit" ? "var(--scarlet-pos)" : "var(--scarlet-neg)" }}>
                    {e.direction === "credit" ? "+" : "-"}{formatCurrency(e.amount)}
                  </span>
                  {isPayment && (
                    invoice ? (
                      <>
                        <button
                          data-testid={`${DESK.ledgerDownloadInvoiceButton(e._id)}-show`}
                          onClick={() => window.open(`${API}/invoices/${invoice.id}/pdf?inline=true`, "_blank")}
                          className="text-xs font-semibold inline-flex items-center gap-1 hover:opacity-70"
                          style={{ color: "#141416" }}
                        >
                          <Eye size={12} /> Show
                        </button>
                        <button
                          data-testid={DESK.ledgerDownloadInvoiceButton(e._id)}
                          onClick={() => window.open(`${API}/invoices/${invoice.id}/pdf`, "_blank")}
                          className="text-xs font-semibold inline-flex items-center gap-1 hover:opacity-70"
                          style={{ color: "#141416" }}
                        >
                          <FileText size={12} /> Invoice
                        </button>
                      </>
                    ) : (
                      <button
                        data-testid={DESK.ledgerGenerateInvoiceButton(e._id)}
                        onClick={() => generateInvoiceMutation.mutate(e.related_ref)}
                        disabled={generateInvoiceMutation.isPending}
                        className="text-xs font-semibold inline-flex items-center gap-1 hover:opacity-70"
                        style={{ color: "#D6202B" }}
                      >
                        <FileText size={12} /> Generate invoice
                      </button>
                    )
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </motion.div>

      <EditMemberModal member={member} open={modal === "edit"} onOpenChange={(v) => setModal(v ? "edit" : null)} />
      <ReactivateModal member={member} open={modal === "reactivate"} onOpenChange={(v) => setModal(v ? "reactivate" : null)} />
      {activeMembership && <RenewModal member={member} membership={activeMembership} open={modal === "renew"} onOpenChange={(v) => setModal(v ? "renew" : null)} />}
      <AddFamilyModal member={member} open={modal === "family"} onOpenChange={(v) => setModal(v ? "family" : null)} />
      <AddPlanModal member={member} open={modal === "addPlan"} onOpenChange={(v) => setModal(v ? "addPlan" : null)} />
      <BookMemberIntoClassDialog
        open={modal === "bookClass"} onOpenChange={(v) => setModal(v ? "bookClass" : null)}
        memberId={memberId} memberName={member.profile.name} branchId={member.branch_id}
        testIds={{ dialog: DESK.memberBookClassDialog, select: DESK.memberBookClassSelect, confirm: DESK.memberBookClassConfirmButton }}
      />
      <SuspendDialog
        open={suspendOpen}
        onOpenChange={setSuspendOpen}
        onConfirm={(reason) => suspendMutation.mutate(reason)}
        isPending={suspendMutation.isPending}
        entityLabel={member.profile.name}
        testIds={{ suspendDialog: DESK.memberSuspendDialog, suspendReasonInput: DESK.memberSuspendReasonInput, suspendConfirmButton: DESK.memberSuspendConfirmButton }}
      />
    </div>
  );
}
